import { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X, Package, Loader2, Plus, ChevronDown } from 'lucide-react';
import { kreatorApi } from '../../../../lib/api';

interface Props {
  integrationId: string;
  value: number[];
  onChange: (ids: number[]) => void;
}

function SelectedProductPill({ integrationId, productId, onRemove }: {
  integrationId: string;
  productId: number;
  onRemove: () => void;
}) {
  const { data: product, isLoading } = useQuery({
    queryKey: ['wc-product', integrationId, productId],
    queryFn: () => kreatorApi.getWcProduct(integrationId, productId).then(r => r.data),
    staleTime: 60_000,
  });

  return (
    <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-surface-hover border border-border">
      <Package className="w-3.5 h-3.5 text-accent-primary shrink-0" />
      <div className="flex-1 min-w-0">
        {isLoading ? (
          <span className="text-xs text-text-secondary">Ładowanie...</span>
        ) : product ? (
          <>
            <span className="text-xs text-text-primary truncate block">{product.name}</span>
            <span className="text-[10px] text-text-secondary">
              {product.price && `${product.price} zł`}
              {product.sku && ` · SKU: ${product.sku}`}
            </span>
          </>
        ) : (
          <span className="text-xs text-text-muted">Produkt #{productId}</span>
        )}
      </div>
      <button
        onClick={onRemove}
        className="p-0.5 text-text-muted hover:text-danger transition-colors shrink-0"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export function WcMultiProductPicker({ integrationId, value, onChange }: Props) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Click outside to close
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Initial products (loaded when dropdown opens without search)
  const { data: initialProducts, isLoading: loadingInitial } = useQuery({
    queryKey: ['wc-products-initial', integrationId],
    queryFn: () => kreatorApi.getWcProducts(integrationId, { status: 'publish', per_page: 20 }).then(r => r.data),
    enabled: isOpen && !!integrationId,
    staleTime: 60_000,
  });

  // Search products (when user types)
  const { data: searchResults, isLoading: searching } = useQuery({
    queryKey: ['wc-products-search', integrationId, debouncedSearch],
    queryFn: () => kreatorApi.getWcProducts(integrationId, { search: debouncedSearch, status: 'publish' }).then(r => r.data),
    enabled: isOpen && debouncedSearch.length >= 2,
    staleTime: 30_000,
  });

  // Use search results if searching, otherwise use initial products
  const displayProducts = debouncedSearch.length >= 2 ? searchResults : initialProducts;
  const isLoadingProducts = debouncedSearch.length >= 2 ? searching : loadingInitial;

  const handleAdd = useCallback((productId: number) => {
    if (!value.includes(productId)) {
      onChange([...value, productId]);
    }
    setSearch('');
    setIsOpen(false);
  }, [value, onChange]);

  const handleRemove = useCallback((productId: number) => {
    onChange(value.filter(id => id !== productId));
  }, [value, onChange]);

  // Filter out already selected products
  const filteredResults = displayProducts?.filter(p => !value.includes(p.id));

  return (
    <div className="space-y-2">
      {/* Selected products list */}
      {value.length > 0 && (
        <div className="space-y-1.5">
          {value.map((productId) => (
            <SelectedProductPill
              key={productId}
              integrationId={integrationId}
              productId={productId}
              onRemove={() => handleRemove(productId)}
            />
          ))}
        </div>
      )}

      {/* Search input to add products */}
      <div ref={containerRef} className="relative">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              if (!isOpen) setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder="Szukaj produktu do dodania..."
            className="w-full pl-8 pr-8 py-1.5 text-xs bg-surface-hover border border-border rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-primary/50"
          />
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-text-muted hover:text-text-primary"
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-lg bg-surface-raised border border-border shadow-xl">
            {isLoadingProducts && !displayProducts && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 text-text-secondary animate-spin" />
              </div>
            )}

            {filteredResults && filteredResults.length === 0 && (
              <div className="px-3 py-3 text-xs text-text-muted text-center">
                {displayProducts && displayProducts.length > 0
                  ? 'Wszystkie wyniki są już dodane'
                  : debouncedSearch.length >= 2
                    ? `Brak wyników dla "${debouncedSearch}"`
                    : 'Brak produktów'}
              </div>
            )}

            {filteredResults?.map((product) => (
              <button
                key={product.id}
                onClick={() => handleAdd(product.id)}
                className="w-full px-3 py-2 text-left hover:bg-surface-hover/50 transition-colors flex items-center gap-2 border-b border-border-subtle last:border-b-0"
              >
                <Plus className="w-3.5 h-3.5 text-accent-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-xs text-text-primary block truncate">{product.name}</span>
                  <span className="text-[10px] text-text-secondary">
                    {product.price && `${product.price} zł`}
                    {product.sku && ` · SKU: ${product.sku}`}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
